import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
const CHECKOUT_URL = 'https://pay.hotmart.com/I103403200A';
type CTAButtonProps = {
  children: React.ReactNode;
  href?: string;
  className?: string;
  size?: 'default' | 'large';
  showPriceBadge?: boolean;
};
export function CTAButton({
  children,
  href = '#pricing',
  className = '',
  size = 'default',
  showPriceBadge = false
}: CTAButtonProps) {
  const {
    t
  } = useLanguage();
  const sizeClasses = size === 'large' ? 'px-10 py-5 text-lg md:text-xl' : 'px-8 py-4 text-base md:text-lg';
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Si el href es #pricing, hacer scroll suave
    if (href === '#pricing') {
      e.preventDefault();
      const element = document.querySelector('#pricing');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    // Para cualquier otro caso (incluyendo href vacío o diferente), abrir checkout en nueva pestaña
    else {
      e.preventDefault();
      // Enviar evento AddToCart a Meta Pixel
      if ((window as any).fbq) {
        ;
        (window as any).fbq('track', 'AddToCart', {
          content_name: 'ROMPE LA MATRIX 3-6-9',
          content_type: 'product',
          value: 7.97,
          currency: 'USD'
        });
        console.log('Meta Pixel: AddToCart event sent');
      }
      // Abrir checkout en nueva pestaña
      window.open(CHECKOUT_URL, '_blank');
    }
  };
  return <div className="inline-flex flex-col items-center gap-4 w-full" data-id="element-5">
      <motion.a href={href === '#pricing' ? href : CHECKOUT_URL} onClick={handleClick} className={`
          relative inline-block font-bold uppercase tracking-wider
          bg-gradient-to-r from-matrix-accent to-matrix-accent-secondary
          text-black rounded-lg overflow-hidden
          cursor-pointer
          w-full text-center
          ${sizeClasses}
          ${className}
        `} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-6">
        {/* Animated shine effect */}
        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" initial={{
        x: '-100%'
      }} animate={{
        x: '200%'
      }} transition={{
        duration: 2,
        repeat: Infinity,
        repeatDelay: 1,
        ease: 'easeInOut'
      }} data-id="element-7" />

        {/* Glow effect on hover */}
        <motion.div className="absolute inset-0 bg-white/20 rounded-lg" initial={{
        opacity: 0
      }} whileHover={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-8" />

        {/* Button content */}
        <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-9">
          {children}
          <motion.span animate={{
          x: [0, 5, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} data-id="element-10">
            →
          </motion.span>
        </span>

        {/* Pulsing glow */}
        <motion.div className="absolute inset-0 rounded-lg" animate={{
        boxShadow: ['0 0 20px rgba(0, 217, 255, 0.5)', '0 0 40px rgba(0, 217, 255, 0.8)', '0 0 20px rgba(0, 217, 255, 0.5)']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-11" />
      </motion.a>

      {/* Price Badge - Optimized design */}
      {showPriceBadge && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3,
      duration: 0.5
    }} className="w-full" data-id="element-12">
          {/* Main price card */}
          <motion.div className="bg-matrix-bg-light/90 backdrop-blur-md border border-matrix-accent/20 rounded-2xl p-5 md:p-6 shadow-xl mb-4" animate={{
        boxShadow: ['0 0 20px rgba(0, 217, 255, 0.15)', '0 0 30px rgba(0, 217, 255, 0.25)', '0 0 20px rgba(0, 217, 255, 0.15)']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-13">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-14">
              {/* Left side - Price info */}
              <div className="flex-1 text-center sm:text-left" data-id="element-15">
                <p className="text-white/50 text-xs font-medium mb-2 uppercase tracking-wide" data-id="element-16">
                  {t.ctaButton.packageValue}
                </p>
                <div className="flex items-baseline gap-3 justify-center sm:justify-start" data-id="element-17">
                  <span className="text-white/30 line-through text-lg font-semibold" data-id="element-18">
                    $47
                  </span>
                  <span className="text-matrix-accent text-4xl md:text-5xl font-black tracking-tight" data-id="element-19">
                    $7.97
                  </span>
                </div>
              </div>

              {/* Right side - Savings badge */}
              <motion.div className="flex items-center gap-2 bg-gradient-to-r from-matrix-accent/15 to-matrix-accent-secondary/15 px-5 py-3 rounded-xl border-2 border-matrix-accent/40" whileHover={{
            scale: 1.02,
            borderColor: 'rgba(0, 217, 255, 0.6)'
          }} transition={{
            duration: 0.2
          }} data-id="element-20">
                <span className="text-matrix-accent text-2xl" data-id="element-21">⚡</span>
                <div className="text-left" data-id="element-22">
                  <p className="text-matrix-accent/80 text-xs font-semibold leading-tight uppercase tracking-wide" data-id="element-23">
                    {t.ctaButton.savings}
                  </p>
                  <p className="text-matrix-accent text-2xl font-black leading-tight" data-id="element-24">
                    $39
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Trust badges - separated below */}
          <div className="flex items-center justify-center gap-6 text-white/50 text-sm pt-2" data-id="element-25">
            <div className="flex items-center gap-2" data-id="element-26">
              <span className="text-matrix-accent text-lg" data-id="element-27">🔒</span>
              <span className="font-medium" data-id="element-28">{t.ctaButton.securePayment}</span>
            </div>
            <div className="w-px h-4 bg-white/20" data-id="element-29" />
            <div className="flex items-center gap-2" data-id="element-30">
              <span className="text-matrix-accent text-lg" data-id="element-31">⚡</span>
              <span className="font-medium" data-id="element-32">{t.ctaButton.instantAccess}</span>
            </div>
          </div>
        </motion.div>}
    </div>;
}